import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      loadInvite();
    }
  }, [token]);

  const loadInvite = async () => {
    try {
      const data = await api.getInvite(token!);
      setInvite(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load invite');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    setError('');

    try {
      await api.acceptInvite(token!);
      const allFamilies = await api.getFamilies().catch(() => []);
      navigate(allFamilies.length > 1 ? '/home' : '/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading invite...</div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Invalid Invite</h2>
            <p className="text-gray-600 mb-6">{error || 'This invite link is not valid.'}</p>
            <button onClick={() => navigate('/dashboard')} className="btn btn-primary w-full">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">You're invited!</h1>
          <p className="text-gray-600 mb-6">
            {invite.createdBy.name} has invited you to join the family coordination for{' '}
            <strong>{invite.family.name}</strong> as a <strong>{invite.role}</strong>.
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accepting ? 'Accepting...' : 'Accept Invite'}
            </button>
            <button onClick={() => navigate('/dashboard')} className="w-full btn btn-secondary">
              Maybe later
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Role: {invite.role}</h3>
            <div className="text-xs text-gray-600 space-y-1">
              {invite.role === 'OWNER' && (
                <>
                  <p>• Full access to all family data</p>
                  <p>• Can invite and remove members</p>
                  <p>• Can edit all tasks, medications, and appointments</p>
                </>
              )}
              {invite.role === 'MEMBER' && (
                <>
                  <p>• Can view all family data</p>
                  <p>• Can edit tasks, medications, and appointments</p>
                  <p>• Cannot invite or remove members</p>
                </>
              )}
              {invite.role === 'VIEWER' && (
                <>
                  <p>• Can view all family data</p>
                  <p>• Cannot edit or create anything</p>
                  <p>• Read-only access</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
